if (!customElements.get('notify-me-modal')) {
  customElements.define(
    'notify-me-modal',
    class NotifyMeModal extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.notify-me-modal__panel');
        this.form = this.querySelector('[data-notify-me-form]');
        this.emailInput = this.querySelector('[name="email"]');
        this.variantInput = this.querySelector('[data-notify-me-variant-id]');
        this.status = this.querySelector('[data-notify-me-status]');
        this.submitButton = this.querySelector('[data-notify-me-submit]');
        this.image = this.querySelector('[data-notify-me-image]');
        this.price = this.querySelector('[data-notify-me-price]');
        this.productTitle = this.querySelector('[data-notify-me-product-title]');
        this.variantDetails = this.querySelector('[data-notify-me-variant-details]');
        this.variants = this.parseJson('[data-notify-me-variants]', []);
        this.options = this.parseJson('[data-notify-me-options]', []);
        this.moneyFormat = window.themeMoneyFormat || '${{amount}}';

        this.querySelectorAll('[data-notify-me-close]').forEach((button) => {
          button.addEventListener('click', this.close.bind(this));
        });
        this.form?.addEventListener('submit', this.onSubmit.bind(this));
        document.addEventListener('click', this.onCardTriggerClick.bind(this));
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
        });
      }

      parseJson(selector, fallback) {
        try {
          const script = this.querySelector(selector);
          return script ? JSON.parse(script.textContent) : fallback;
        } catch (error) {
          return fallback;
        }
      }

      open(variantId, trigger) {
        this.trigger = trigger;
        if (trigger?.matches?.('[data-notify-me-card-trigger]')) {
          this.updateFromCardTrigger(trigger, variantId);
        } else {
          this.updateVariant(variantId);
        }
        this.centerPanel();
        this.classList.add('is-open');
        this.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
        this.emailInput?.focus();
      }

      onCardTriggerClick(event) {
        const trigger = event.target.closest('[data-notify-me-card-trigger]');
        if (!trigger || trigger.getAttribute('aria-controls') !== this.id) return;

        event.preventDefault();
        this.open(trigger.dataset.variantId, trigger);
      }

      centerPanel() {
        if (!this.panel) return;

        this.panel.style.setProperty('position', 'fixed', 'important');
        this.panel.style.setProperty('top', '50%', 'important');
        this.panel.style.setProperty('left', '50%', 'important');
        this.panel.style.setProperty('right', 'auto', 'important');
        this.panel.style.setProperty('bottom', 'auto', 'important');
        this.panel.style.setProperty('height', 'auto', 'important');
        this.panel.style.setProperty('max-height', 'calc(100vh - 4rem)', 'important');
        this.panel.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
      }

      close() {
        this.classList.remove('is-open');
        this.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
        this.status.textContent = '';
        this.form?.classList.remove('is-success');
        this.form?.reset();
        const focusTarget = this.trigger?.submitButton || (this.trigger instanceof HTMLElement ? this.trigger : null);
        focusTarget?.focus();
      }

      updateFromCardTrigger(trigger, variantId) {
        if (this.variantInput) this.variantInput.value = variantId || trigger.dataset.variantId;
        if (this.productTitle && trigger.dataset.productTitle) this.productTitle.textContent = trigger.dataset.productTitle;
        if (this.price && trigger.dataset.productPrice) this.price.textContent = trigger.dataset.productPrice;
        if (this.variantDetails) this.variantDetails.innerHTML = '';
        if (this.image && trigger.dataset.productImage) {
          this.image.src = trigger.dataset.productImage;
          this.image.alt = trigger.dataset.productTitle || '';
        }
      }

      updateVariant(variantId) {
        const variant = this.variants.find((item) => String(item.id) === String(variantId)) || this.variants[0];
        if (!variant) return;

        this.variantInput.value = variant.id;
        this.price.textContent = this.formatMoney(variant.price);
        this.variantDetails.innerHTML = this.options
          .map((name, index) => {
            const value = variant.options[index];
            if (!value || value === 'Default Title') return '';
            return `<div><dt>${this.escapeHtml(name)}:</dt><dd>${this.escapeHtml(value)}</dd></div>`;
          })
          .join('');

        const imageSrc = variant.featured_image?.src;
        if (imageSrc && this.image) this.image.src = imageSrc;
      }

      async onSubmit(event) {
        event.preventDefault();

        if (!this.emailInput.checkValidity()) {
          this.emailInput.reportValidity();
          return;
        }

        this.setLoading(true);
        this.status.textContent = '';

        const formData = new FormData(this.form);
        const variantId = formData.get('variant');

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.klaviyoPublicKey)}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/vnd.api+json',
                'revision': '2023-12-15',
              },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    channels: ['EMAIL'],
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: { email: formData.get('email') },
                      },
                    },
                  },
                  relationships: {
                    variant: {
                      data: {
                        type: 'catalog-variant',
                        id: `$shopify:::$default:::${variantId}`,
                      },
                    },
                  },
                },
              }),
            }
          );

          if (!response.ok) {
            const errText = await response.text();
            console.error('Klaviyo error', response.status, errText);
            throw new Error('Klaviyo back-in-stock request failed');
          }

          if (formData.get('marketing_opt_in') === 'true') {
            window._learnq = window._learnq || [];
            window._learnq.push([
              'identify',
              {
                $email: formData.get('email'),
                marketing_opt_in_from_back_in_stock: true,
              },
            ]);
          }

          this.status.textContent = window.notifyMeStrings?.success || "You're on the list. We'll let you know when this piece is back in stock.";
          this.form.classList.add('is-success');
          this.form.reset();
        } catch (error) {
          this.status.textContent = window.notifyMeStrings?.error || "We couldn't save your request. Please try again.";
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.submitButton.toggleAttribute('disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, (character) => {
          return {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
          }[character];
        });
      }
    }
  );
}
